import { useEffect, useRef } from 'react';
import { useWorkflowData, useWorkflowProps } from '../../api';
import { useAppDispatch } from '~/store/reduxHooks';
import { initWorkflowPropsAndData } from '../../store/redux/slice';
import { captureExceptionsToSentry } from '~/helpers/exceptionHandler';
import type { Evaluation } from '~/entities/types';
import type { FeatureFlags } from '~/helpers/typescript';

/**
 * For use outside the workflow editor, when you want to spin up redux state for a workflow version
 */
export function useInitializeWorkflowRedux({
  workflowToken,
  versionId,
  featureFlags,
  enabled = true,
  reinitialize = false,
}: {
  workflowToken: Evaluation['application_token'];
  versionId: Evaluation['public_version'];
  featureFlags: Partial<FeatureFlags>;
  enabled?: boolean;
  reinitialize?: boolean;
}) {
  const initializedWorkflowRef = useRef<{
    token: string | null;
    version: number | null;
  }>({ token: null, version: null });

  const dispatch = useAppDispatch();

  const workflowData = useWorkflowData({
    versionId,
    workflowToken,
    enabled,
  });

  const workflowProps = useWorkflowProps({
    versionId,
    workflowToken,
    enabled,
  });

  useEffect(() => {
    if (!workflowData.data || !workflowProps.data) {
      return;
    }

    const isNewWorkflow = (
      initializedWorkflowRef.current.token !== workflowToken
      || initializedWorkflowRef.current.version !== versionId
    );

    if (isNewWorkflow || reinitialize) {
      try {
        dispatch(initWorkflowPropsAndData({
          workflowData: workflowData.data,
          workflowProps: workflowProps.data,
          featureFlags,
        }));
        initializedWorkflowRef.current = { token: workflowToken, version: versionId };
      } catch (error) {
        captureExceptionsToSentry(error);
        // eslint-disable-next-line no-console
        console.error(`Error transforming workflow data: ${error}`);
      }
    }
  }, [
    dispatch,
    workflowData.data,
    workflowProps.data,
    featureFlags,
    reinitialize,
    workflowToken,
    versionId,
  ]);

  return initializedWorkflowRef.current.token !== null;
}
